import React, { useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { DownloadIcon, ListChecksIcon, MoreVerticalIcon, SearchIcon, Settings2Icon, TagIcon, UsersIcon } from 'lucide-react';
import { useAuth, useWorkspace } from '../../contexts/AuthContext';
import { bulkUpdateStage, deleteLead, updateLeadStage } from '../../services/leadService';
import { usePreferences } from '../../hooks/usePreferences';
import { DEFAULT_LEAD_COLUMNS, DEFAULT_LEADS_PER_PAGE, LEADS_PER_PAGE_OPTIONS } from '../../services/preferencesService';
import { FloatingPanel } from '../../components/shared/FloatingPanel';
import { downloadCsv } from '../../utils/csv';
import { formatDate, formatDateTime, fullName } from '../../utils/format';
import { Lead, LeadNote } from '../../types';
import { PageHeader } from '../../components/crm/PageHeader';
import { DemoBadge } from '../../components/crm/DemoBadge';
import { EmptyState } from '../../components/crm/EmptyState';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Checkbox } from '../../components/ui/Checkbox';
import { Table } from '../../components/ui/Table';
import { Dropdown } from '../../components/ui/Dropdown';
import { SourceBadge } from '../../components/crm/SourceBadge';
import { StageBadge } from '../../components/crm/StageBadge';
import { StageManager } from '../../components/crm/StageManager';
import { SourceManager } from '../../components/crm/SourceManager';
import { ConfirmDialog } from '../../components/crm/ConfirmDialog';
import { noteColor } from '../../components/crm/noteColors';
import { LeadDrawer } from '../../components/crm/LeadDrawer';

/**
 * Paginación en cliente mientras los datos son de demostración.
 *
 * TODO(Supabase): sustituir por una consulta paginada en servidor
 * (`.range(offset, offset + limit - 1)`) que reciba los mismos filtros,
 * búsqueda y orden, para no descargar todos los leads al navegador.
 * `pageSize` y `currentPage` ya son los únicos valores necesarios.
 */

/**
 * Columnas disponibles en la tabla de leads.
 * TODO(Supabase): guardar la selección por usuario en `user_preferences`.
 */
const COLUMN_OPTIONS: {
  key: string;
  label: string;
}[] = [{
  key: 'name',
  label: 'Nombre'
}, {
  key: 'company',
  label: 'Empresa'
}, {
  key: 'email',
  label: 'Email'
}, {
  key: 'phone',
  label: 'Teléfono'
}, {
  key: 'instagram',
  label: 'Instagram'
}, {
  key: 'city',
  label: 'Ciudad'
}, {
  key: 'service',
  label: 'Tipo de servicio'
}, {
  key: 'adBudget',
  label: 'Inversión actual'
}, {
  key: 'budget',
  label: 'Presupuesto disponible'
}, {
  key: 'source',
  label: 'Fuente'
}, {
  key: 'campaign',
  label: 'Campaña'
}, {
  key: 'landing',
  label: 'Landing'
}, {
  key: 'form',
  label: 'Formulario'
}, {
  key: 'stage',
  label: 'Estado'
}, {
  key: 'owner',
  label: 'Comercial'
}, {
  key: 'createdAt',
  label: 'Fecha'
}, {
  key: 'lastContact',
  label: 'Último contacto'
}, {
  key: 'note',
  label: 'Última nota'
}];
/** Columnas activas la primera vez, en este orden. */
const DEFAULT_COLUMNS = DEFAULT_LEAD_COLUMNS;
export function Leads() {
  const {
    user,
    hasPermission
  } = useAuth();
  const {
    organizationId,
    leads,
    stages,
    sources,
    forms,
    landings,
    notes,
    isDemo
  } = useWorkspace();
  const members = useWorkspaceMembers();
  const [drawerLead, setDrawerLead] = useState<string | null>(null);
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [stagesOpen, setStagesOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [confirmTrash, setConfirmTrash] = useState<Lead | null>(null);
  const [trashing, setTrashing] = useState(false);
  const [preferences, updatePreferences] = usePreferences();
  const visibleColumns = preferences.leadColumns;
  const setVisibleColumns = (updater: string[] | ((prev: string[]) => string[])) => updatePreferences({
    leadColumns: typeof updater === 'function' ? updater(visibleColumns) : updater
  });
  const authorName = user ? fullName(user.firstName, user.lastName) : 'Sistema';
  const [search, setSearch] = useState('');
  const [stageId, setStageId] = useState('');
  const [formId, setFormId] = useState('');
  const [landingId, setLandingId] = useState('');
  const [campaign, setCampaign] = useState('');
  const [owner, setOwner] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<string[]>([]);
  const [bulkStage, setBulkStage] = useState('');
  const [applying, setApplying] = useState(false);
  const campaigns = useMemo(() => [...new Set(leads.map(l => l.origin.campaign).filter(Boolean))], [leads]);
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return leads.filter(lead => {
      if (term && ![lead.firstName, lead.lastName, lead.company, lead.email, lead.phone].join(' ').toLowerCase().includes(term)) return false;
      if (stageId && lead.stageId !== stageId) return false;
      if (formId && lead.origin.formId !== formId) return false;
      if (landingId && lead.origin.landingId !== landingId) return false;
      if (campaign && lead.origin.campaign !== campaign) return false;
      if (owner && lead.assignedTo !== owner) return false;
      if (from && lead.createdAt < from) return false;
      if (to && lead.createdAt > `${to}T23:59:59`) return false;
      return true;
    });
  }, [leads, search, stageId, formId, landingId, campaign, owner, from, to]);
  const pageSize = preferences.leadsPerPage || DEFAULT_LEADS_PER_PAGE;
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const offset = (currentPage - 1) * pageSize;
  const paged = filtered.slice(offset, offset + pageSize);
  const rangeFrom = filtered.length === 0 ? 0 : offset + 1;
  const rangeTo = Math.min(offset + pageSize, filtered.length);

  /** Números de página a mostrar, con la actual siempre centrada. */
  const pageNumbers = useMemo(() => {
    const max = 5;
    if (totalPages <= max) return Array.from({
      length: totalPages
    }, (_, i) => i + 1);
    const start = Math.max(1, Math.min(currentPage - 2, totalPages - max + 1));
    return Array.from({
      length: max
    }, (_, i) => start + i);
  }, [currentPage, totalPages]);
  function stageName(lead: Lead) {
    return stages.find(s => s.id === lead.stageId)?.name ?? 'Nuevo';
  }
  function memberName(id?: string) {
    const member = members.find(m => m.userId === id);
    return member ? fullName(member.firstName, member.lastName) : 'Sin asignar';
  }

  /** Última nota del lead, para la vista previa en la tabla. */
  function lastNote(leadId: string) {
    return notes.filter(n => n.leadId === leadId).sort((a, b) => b.createdAt.localeCompare(a.createdAt))[0];
  }
  function exportCsv() {
    downloadCsv('leads-ava.csv', ['Nombre', 'Apellidos', 'Empresa', 'Email', 'Teléfono', 'Instagram', 'Ciudad', 'País', 'Servicio solicitado', 'Presupuesto publicidad', 'Presupuesto disponible', 'Formulario', 'Landing', 'Fuente', 'Campaña', 'Estado', 'Comercial', 'Fecha', 'Última nota'], filtered.map(lead => [lead.firstName, lead.lastName, lead.company, lead.email, lead.phone, lead.instagram ?? '', lead.city ?? '', lead.country ?? '', lead.service ?? '', lead.adBudget ?? '', lead.budget ?? '', forms.find(f => f.id === lead.origin.formId)?.name ?? '', landings.find(l => l.id === lead.origin.landingId)?.name ?? '', lead.origin.source, lead.origin.campaign, stageName(lead), memberName(lead.assignedTo), formatDate(lead.createdAt), lastNote(lead.id)?.body ?? '']));
  }

  /** Menú de acciones rápidas de cada fila o tarjeta. */
  function rowActions(lead: Lead) {
    const items = [{
      label: 'Abrir lead',
      onSelect: () => setDrawerLead(lead.id)
    }, {
      label: 'Abrir WhatsApp',
      onSelect: () => window.open(`https://wa.me/${lead.phone.replace(/\D/g, '')}`, '_blank', 'noopener')
    }, {
      label: 'Copiar teléfono',
      onSelect: () => navigator.clipboard?.writeText(lead.phone)
    }];
    if (hasPermission('leads.edit')) {
      items.splice(1, 0, {
        label: 'Añadir nota',
        onSelect: () => setDrawerLead(lead.id)
      });
    }
    if (hasPermission('leads.delete')) {
      items.push({
        label: 'Mover a papelera',
        destructive: true,
        onSelect: () => setConfirmTrash(lead)
      } as never);
    }
    return items;
  }
  async function applyBulk() {
    if (!bulkStage || selected.length === 0) return;
    setApplying(true);
    await bulkUpdateStage(selected, bulkStage, user ? fullName(user.firstName, user.lastName) : 'Sistema');
    setApplying(false);
    setSelected([]);
    setBulkStage('');
  }
  if (leads.length === 0) {
    return <>
        <PageHeader title="Leads" description="Contactos recibidos por tu empresa." data-id="element-1331" data-mp="394kgjbpuvqq3" />
        <EmptyState icon={<UsersIcon className="h-5 w-5" data-id="element-1333" data-mp="349odlgj2ziy7" />} title="Todavía no tienes contactos" description="Publica una landing o comparte un formulario para comenzar a recibir leads." action={<Link to="/app/landings" data-id="element-1334" data-mp="2vcdytl721boj">
              <Button data-id="element-1335" data-mp="2v99pyain9rrj">Ir a landings</Button>
            </Link>} data-id="element-1332" data-mp="34a7ssphcjt8j" />
      </>;
  }
  return <>
      <PageHeader title="Leads" description={`${filtered.length} contactos que cumplen los filtros.`} actions={<>
            {isDemo && <DemoBadge className="self-center" data-id="element-1337" data-mp="3j0f66qshym2d" />}
            <Button variant="secondary" leftIcon={<DownloadIcon className="h-4 w-4" data-id="element-1339" data-mp="3e1km7lismrgo" />} onClick={exportCsv} data-id="element-1338" data-mp="3iz41mor89aa7">
              Exportar CSV
            </Button>
            <Button variant="secondary" leftIcon={<Settings2Icon className="h-4 w-4" data-id="element-1341" data-mp="27n39czv5n3pp" />} onClick={() => setColumnsOpen(true)} data-id="element-1340" data-mp="27m1ltvwycor3">
              Personalizar columnas
            </Button>
            <Button variant="secondary" leftIcon={<ListChecksIcon className="h-4 w-4" data-id="element-1343" data-mp="9fbzhhq1pcy6" />} onClick={() => setStagesOpen(true)} data-id="element-1342" data-mp="9eaf2mn53e3s">
              Gestionar estados
            </Button>
            <Button variant="secondary" leftIcon={<TagIcon className="h-4 w-4" data-id="element-1345" data-mp="i743paoh7pub" />} onClick={() => setSourcesOpen(true)} data-id="element-1344" data-mp="4dcvq6qe8e0p">
              Gestionar fuentes
            </Button>
          </>} data-id="element-1336" data-mp="3rz97gugrw0m9" />

      {/* Filtros */}
      <div className="mb-5 rounded-2xl border border-border bg-background p-5 shadow-soft" data-id="element-1346" data-mp="s4s2cbp7dhhr">
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" data-id="element-1347" data-mp="s6lci0z0yjui">
          {/* Buscador: la lupa va absolutamente posicionada y el campo
              reserva su espacio con padding, para que nunca se solapen. */}
          <div className="relative" data-id="element-1348" data-mp="x44osvc5wmjt">
            <SearchIcon className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" data-id="element-1349" data-mp="x56bxy6gczhw" />
            <input id="buscar" type="search" aria-label="Buscar leads" placeholder="Buscar por nombre, empresa o email" value={search} onChange={e => {
            setSearch(e.target.value);
            setPage(1);
          }} className="h-11 w-full rounded-xl border border-border bg-background pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" data-id="element-1350" data-mp="2yv80lnxsg75y" />
          </div>
          <Select id="f-estado" placeholder="Todos los estados" options={stages.map(s => ({
          value: s.id,
          label: s.name
        }))} value={stageId} onChange={e => setStageId(e.target.value)} data-id="element-1351" data-mp="2jz03xbqyfyp7" />
          <Select id="f-form" placeholder="Todos los formularios" options={forms.map(f => ({
          value: f.id,
          label: f.name
        }))} value={formId} onChange={e => setFormId(e.target.value)} data-id="element-1352" data-mp="2a3op6tfjqrhg" />
          <Select id="f-landing" placeholder="Todas las landings" options={landings.map(l => ({
          value: l.id,
          label: l.name
        }))} value={landingId} onChange={e => setLandingId(e.target.value)} data-id="element-1353" data-mp="213taiems8eco" />
          <Select id="f-campana" placeholder="Todas las campañas" options={campaigns.map(c => ({
          value: c,
          label: c
        }))} value={campaign} onChange={e => setCampaign(e.target.value)} data-id="element-1354" data-mp="3t0tdbub1owx7" />
          <Select id="f-comercial" placeholder="Todos los comerciales" options={members.map(m => ({
          value: m.userId,
          label: fullName(m.firstName, m.lastName)
        }))} value={owner} onChange={e => setOwner(e.target.value)} data-id="element-1355" data-mp="3jzv858ovejd1" />
          <Input id="f-desde" type="date" label="Desde" value={from} onChange={e => setFrom(e.target.value)} data-id="element-1356" data-mp="giv4ck204uv3" />
          <Input id="f-hasta" type="date" label="Hasta" value={to} onChange={e => setTo(e.target.value)} data-id="element-1357" data-mp="bmo7rhvhwvpt" />
        </div>
      </div>

      {/* Acciones en lote */}
      {selected.length > 0 && hasPermission('leads.edit') && <div className="mb-4 flex flex-col gap-3 rounded-lg border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center" data-id="element-1358" data-mp="za5ugyzwl3lx">
          <p className="text-sm font-medium text-foreground" data-id="element-1359" data-mp="uj5zw0e9axml">
            {selected.length} leads seleccionados
          </p>
          <div className="flex flex-1 flex-col gap-2 sm:flex-row sm:justify-end" data-id="element-1360" data-mp="11w56mqes3mcl">
            <Select id="bulk" placeholder="Cambiar estado a…" options={stages.map(s => ({
          value: s.id,
          label: s.name
        }))} value={bulkStage} onChange={e => setBulkStage(e.target.value)} data-id="element-1361" data-mp="16uq8svdlcg3o" />
            <Button onClick={applyBulk} loading={applying} disabled={!bulkStage} data-id="element-1362" data-mp="1gtfnyge3rmg8">
              Aplicar
            </Button>
            <Button variant="ghost" onClick={() => setSelected([])} data-id="element-1363" data-mp="1krlsrivdrryi">
              Cancelar
            </Button>
          </div>
        </div>}

      {/* Tabla escritorio */}
      <div className="hidden lg:block" data-id="element-1364" data-mp="1ujri0ku8zva0">
        <Table data={paged} rowKey={lead => lead.id} selectable={hasPermission('leads.edit')} selectedKeys={selected} onSelectionChange={setSelected} emptyMessage="Ningún lead cumple estos filtros." columns={[{
        key: 'name',
        header: 'Nombre',
        sortable: true,
        render: lead => <button type="button" onClick={() => setDrawerLead(lead.id)} className="text-left font-medium text-foreground hover:text-primary" data-id="element-1366" data-mp="367x5c7p1g3iz">
                  {fullName(lead.firstName, lead.lastName)}
                </button>
      }, {
        key: 'company',
        header: 'Empresa',
        render: lead => lead.company
      }, {
        key: 'phone',
        header: 'Teléfono',
        render: lead => lead.phone
      }, {
        key: 'email',
        header: 'Email',
        render: lead => lead.email
      }, {
        key: 'instagram',
        header: 'Instagram',
        render: lead => lead.instagram || '—'
      }, {
        key: 'city',
        header: 'Ciudad',
        render: lead => lead.city || '—'
      }, {
        key: 'service',
        header: 'Servicio solicitado',
        render: lead => lead.service || '—'
      }, {
        key: 'adBudget',
        header: 'Presup. publicidad',
        render: lead => lead.adBudget || '—'
      }, {
        key: 'budget',
        header: 'Presup. disponible',
        render: lead => lead.budget || '—'
      }, {
        key: 'source',
        header: 'Fuente',
        render: lead => <SourceBadge source={lead.origin.source} sources={sources} data-id="element-1367" data-mp="j3su82xp5oie" />
      }, {
        key: 'stage',
        header: 'Estado',
        render: lead => <StageBadge stage={stages.find(s => s.id === lead.stageId)} stages={hasPermission('leads.edit') ? stages : undefined} onChange={id => updateLeadStage(lead.id, id, authorName)} data-id="element-1368" data-mp="a2bhmuji04yg" />
      }, {
        key: 'owner',
        header: 'Comercial',
        render: lead => memberName(lead.assignedTo)
      }, {
        key: 'campaign',
        header: 'Campaña',
        render: lead => lead.origin.campaign || '—'
      }, {
        key: 'landing',
        header: 'Landing',
        render: lead => landings.find(l => l.id === lead.origin.landingId)?.name ?? '—'
      }, {
        key: 'form',
        header: 'Formulario',
        render: lead => forms.find(f => f.id === lead.origin.formId)?.name ?? '—'
      }, {
        key: 'createdAt',
        header: 'Fecha',
        sortable: true,
        render: lead => formatDate(lead.createdAt)
      }, {
        key: 'lastContact',
        header: 'Último contacto',
        render: lead => lead.lastContactAt ? formatDate(lead.lastContactAt) : 'Sin contactar'
      }, {
        key: 'note',
        header: 'Última nota',
        render: lead => <NotePreview note={lastNote(lead.id)} data-id="element-1369" data-mp="1hg1xh0t6ijpy" />
      }, {
        key: 'actions',
        header: '',
        render: lead => <Dropdown align="right" trigger={<span className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-muted-foreground hover:bg-surface" data-id="element-1371" data-mp="17muu1li01dsi">
                      <MoreVerticalIcon className="h-4 w-4" aria-hidden="true" data-id="element-1372" data-mp="12mqgo4ce0z6i" />
                    </span>} items={rowActions(lead)} data-id="element-1370" data-mp="17o5ylnj9qpko" />
      }].filter(column => column.key === 'actions' || visibleColumns.includes(column.key))} data-id="element-1365" data-mp="1zicimldewgyz" />
      </div>

      {/* Tarjetas móvil */}
      <ul className="grid gap-3 lg:hidden" data-id="element-1373" data-mp="oq38f5ezimla">
        {paged.map(lead => {
        const note = lastNote(lead.id);
        const expanded = expandedCard === lead.id;
        return <li key={lead.id} className="rounded-2xl border border-border bg-background p-5 shadow-soft" data-id="element-1374" data-mp="35ayqrxec27ru">
              <div className="flex items-start justify-between gap-3" data-id="element-1375" data-mp="39cjwmysg9lsj">
                <div className="min-w-0" data-id="element-1376" data-mp="3ec6btdru9c3z">
                  <p className="truncate font-semibold text-foreground" data-id="element-1377" data-mp="3eb3nndlwhnrr">
                    {fullName(lead.firstName, lead.lastName)}
                  </p>
                  <p className="truncate text-sm text-muted-foreground" data-id="element-1378" data-mp="2lnh9jbf1r0qn">
                    {lead.company}
                  </p>
                </div>
                <Dropdown align="right" trigger={<span className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground" data-id="element-1380" data-mp="2qkhhxszdcfkx">
                      <MoreVerticalIcon className="h-4 w-4" aria-hidden="true" data-id="element-1381" data-mp="1xy6f0boai2ky" />
                    </span>} items={rowActions(lead)} data-id="element-1379" data-mp="2qlsk5od61f9x" />
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2" data-id="element-1382" data-mp="22vf12nck6qrh">
                <StageBadge stage={stages.find(s => s.id === lead.stageId)} stages={hasPermission('leads.edit') ? stages : undefined} onChange={id => updateLeadStage(lead.id, id, authorName)} data-id="element-1383" data-mp="w8vv8wp57579" />
                <SourceBadge source={lead.origin.source} sources={sources} data-id="element-1384" data-mp="15aluupt6hag4" />
              </div>

              {note && <div className={`mt-3 rounded-lg border-l-4 px-3 py-2 ${noteColor(note.color).bg} ${noteColor(note.color).border}`} data-id="element-1385" data-mp="118iepnvkdhoi">
                  <p className="line-clamp-2 text-xs leading-snug text-foreground" data-id="element-1386" data-mp="1f6oxrxvutd44">
                    {note.body}
                  </p>
                </div>}

              {expanded && <dl className="mt-3 grid grid-cols-2 gap-2 border-t border-border pt-3 text-xs text-muted-foreground" data-id="element-1387" data-mp="1oxb5z718r1cs">
                  <div className="truncate" data-id="element-1388" data-mp="1jzrpe0h2mcho">{lead.email}</div>
                  <div className="truncate" data-id="element-1389" data-mp="1xy9cbvz5jkyj">{lead.phone}</div>
                  <div className="truncate" data-id="element-1390" data-mp="1xwoffzpd7pxi">{lead.city || '—'}</div>
                  <div className="truncate" data-id="element-1391" data-mp="1t1iwn9r9erdn">{lead.service || '—'}</div>
                  <div className="truncate" data-id="element-1392" data-mp="2clgfvgmb5gne">{lead.budget || '—'}</div>
                  <div className="truncate" data-id="element-1393" data-mp="2hksy1o28hzjn">{formatDate(lead.createdAt)}</div>
                </dl>}

              <div className="mt-3 flex gap-2" data-id="element-1394" data-mp="23l1qa52b9fe3">
                <Button variant="ghost" size="sm" className="min-h-[44px] flex-1" onClick={() => setExpandedCard(expanded ? null : lead.id)} data-id="element-1395" data-mp="23k9vuxkg9npe">
                  {expanded ? 'Ver menos' : 'Ver más'}
                </Button>
                <Button variant="secondary" size="sm" className="min-h-[44px] flex-1" onClick={() => setDrawerLead(lead.id)} data-id="element-1396" data-mp="1ttx9r0wzlmh5">
                  Abrir ficha
                </Button>
              </div>
            </li>;
      })}
      </ul>

      {/* Paginación */}
      <nav className="mt-5 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between" aria-label="Paginación de leads" data-id="element-1397" data-mp="1ovdm8w2qw2mo">
        <p className="order-2 text-sm text-muted-foreground lg:order-1" data-id="element-1398" data-mp="1aww37bnsbeb3">
          <span className="hidden sm:inline" data-id="element-1399" data-mp="1fufkkkfs4779">
            Mostrando {rangeFrom}–{rangeTo} de {filtered.length} contactos
          </span>
          <span className="sm:hidden" data-id="element-1400" data-mp="1e4on4z4tj02j">
            {rangeFrom}–{rangeTo} de {filtered.length}
          </span>
        </p>

        <div className="order-3 flex flex-wrap items-center justify-center gap-2 lg:order-2" data-id="element-1401" data-mp="151vxf0baawet">
          <Button variant="secondary" size="sm" className="min-h-[44px]" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)} data-id="element-1402" data-mp="1a0z1oijfwylq">
            Anterior
          </Button>
          {totalPages > 1 && <ul className="hidden items-center gap-1 sm:flex" data-id="element-1403" data-mp="21s53d3et74d4">
              {pageNumbers.map(number => <li key={number} data-id="element-1404" data-mp="21tzjm4f4y8av">
                  <button type="button" aria-label={`Ir a la página ${number}`} aria-current={number === currentPage ? 'page' : undefined} onClick={() => setPage(number)} className={`min-h-[44px] min-w-[44px] rounded-xl px-3 text-sm font-medium transition-colors ${number === currentPage ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-surface hover:text-foreground'}`} data-id="element-1405" data-mp="1o1jixa9pimd4">
                    {number}
                  </button>
                </li>)}
            </ul>}
          <span className="text-sm text-muted-foreground sm:hidden" data-id="element-1406" data-mp="2qtvt51pa9jaj">
            {currentPage} / {totalPages}
          </span>
          <Button variant="secondary" size="sm" className="min-h-[44px]" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)} data-id="element-1407" data-mp="34jfqtt40bqcz">
            Siguiente
          </Button>
        </div>

        {/* Contactos por página */}
        <div className="order-1 flex items-center gap-2 lg:order-3" data-id="element-1408" data-mp="2bxwm7udwtp6z">
          <label htmlFor="leads-per-page" className="whitespace-nowrap text-sm text-muted-foreground" data-id="element-1409" data-mp="27w2sy761nngu">
            Contactos por página
          </label>
          <select id="leads-per-page" value={pageSize} onChange={e => {
          updatePreferences({
            leadsPerPage: Number(e.target.value)
          });
          setPage(1);
        }} className="h-11 rounded-xl border border-border bg-background px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" data-id="element-1410" data-mp="2gun48n99yuv1">
            {LEADS_PER_PAGE_OPTIONS.map(option => <option key={option} value={option} data-id="element-1411" data-mp="86p62svf9ywi">
                {option}
              </option>)}
          </select>
        </div>
      </nav>

      <LeadDrawer leadId={drawerLead} onClose={() => setDrawerLead(null)} data-id="element-1412" data-mp="d74k7mnj3afj" />

      <StageManager open={stagesOpen} onClose={() => setStagesOpen(false)} organizationId={organizationId} stages={stages} data-id="element-1413" data-mp="i43dk2zdbdco" />
      <SourceManager open={sourcesOpen} onClose={() => setSourcesOpen(false)} organizationId={organizationId} sources={sources} data-id="element-1414" data-mp="3gov4xlzfbmr5" />

      <ConfirmDialog open={Boolean(confirmTrash)} title="Mover a la papelera" description="El lead saldrá de Leads y del embudo, pero conservará sus notas, respuestas e historial. Podrás restaurarlo desde la Papelera." confirmLabel="Mover a la papelera" loading={trashing} onCancel={() => setConfirmTrash(null)} onConfirm={async () => {
      if (!confirmTrash) return;
      setTrashing(true);
      await deleteLead(confirmTrash.id, authorName);
      setTrashing(false);
      setConfirmTrash(null);
      if (drawerLead === confirmTrash.id) setDrawerLead(null);
    }} data-id="element-1415" data-mp="3ql7uq8rulggm" />

      {/* Panel flotante: se puede mover para no tapar la tabla y los cambios
          se reflejan al instante detrás. */}
      <FloatingPanel open={columnsOpen} title="Personalizar columnas" description="Los cambios se aplican en la tabla al instante." position={preferences.columnPanelPosition} onPositionChange={position => updatePreferences({
      columnPanelPosition: position
    })} onClose={() => setColumnsOpen(false)} footer={<div className="flex flex-wrap gap-2" data-id="element-1417" data-mp="22n73gwjtnswi">
            <Button size="sm" variant="ghost" onClick={() => setVisibleColumns(COLUMN_OPTIONS.map(c => c.key))} data-id="element-1418" data-mp="22nytlrungyfe">
              Mostrar todas
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setVisibleColumns([...DEFAULT_COLUMNS])} data-id="element-1419" data-mp="1spixtbkoh0zk">
              Restaurar
            </Button>
            <Button size="sm" onClick={() => setColumnsOpen(false)} data-id="element-1420" data-mp="1opsd9plqvh3h">
              Listo
            </Button>
          </div>} data-id="element-1416" data-mp="2gevesxm8ydcf">
        <ul className="grid gap-2" data-id="element-1421" data-mp="2idewbazr1kjs">
          {COLUMN_OPTIONS.map(column => <li key={column.key} data-id="element-1422" data-mp="2icn141a2dou1">
              <Checkbox id={`col-${column.key}`} label={column.label} checked={visibleColumns.includes(column.key)} onChange={e => setVisibleColumns(prev => e.target.checked ? [...prev, column.key] : prev.filter(k => k !== column.key))} data-id="element-1423" data-mp="24m9uc28wsg6a" />
            </li>)}
        </ul>
      </FloatingPanel>
    </>;
}

/**
 * Vista previa de la nota más reciente: dos líneas como máximo y la nota
 * completa al pasar el cursor o al pulsar en móvil.
 */
function NotePreview({
  note
}: {
  note?: LeadNote;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const anchor = useRef<HTMLSpanElement>(null);

  /**
   * El contenido completo se pinta con un portal en `document.body` y posición
   * fija, así nunca queda recortado dentro de la fila ni por el scroll de la
   * tabla. Si no cabe debajo, se coloca encima.
   */
  function show() {
    const box = anchor.current?.getBoundingClientRect();
    if (!box) return;
    const width = 288;
    const below = window.innerHeight - box.bottom > 200;
    setPos({
      top: below ? box.bottom + 8 : Math.max(8, box.top - 208),
      left: Math.min(Math.max(8, box.left), window.innerWidth - width - 8)
    });
    setOpen(true);
  }
  if (!note) return <span className="text-muted-foreground" data-id="element-1424" data-mp="1s5imjck0hknh">Sin notas</span>;
  const style = noteColor(note.color);
  const preview = note.body.length > 90 ? `${note.body.slice(0, 90).trim()}…` : note.body;
  return <span ref={anchor} className="block w-56" onMouseEnter={show} onMouseLeave={() => setOpen(false)} data-id="element-1425" data-mp="213azia29gg7p">
      <button type="button" onClick={e => {
      e.stopPropagation();
      if (open) setOpen(false);else show();
    }} className={`flex w-full items-start gap-2 rounded-lg border-l-4 px-2.5 py-2 text-left ${style.bg} ${style.border}`} data-id="element-1426" data-mp="1w2wqopsu528m">
        <span className={`mt-1 h-2 w-2 flex-none rounded-full ${style.dot}`} aria-hidden="true" data-id="element-1427" data-mp="21traxwmrf5bt" />
        <span className="min-w-0" data-id="element-1428" data-mp="26rsvyn3qxepx">
          {note.title && <span className="block truncate text-xs font-semibold text-foreground" data-id="element-1429" data-mp="26skqdullx6em">
              {note.title}
            </span>}
          <span className="line-clamp-2 text-xs leading-snug text-foreground/80" data-id="element-1430" data-mp="2ft0t31uj09if">
            {preview}
          </span>
        </span>
      </button>
      {open && pos && createPortal(<span style={{
      top: pos.top,
      left: pos.left
    }} className={`fixed z-[70] block max-h-52 w-72 overflow-auto rounded-xl border border-border border-l-4 p-3 text-xs leading-relaxed text-foreground shadow-xl ${style.bg} ${style.border}`} data-id="element-1431" data-mp="2kpzjpaezdybv">
            <span className="mb-1 block text-[11px] font-medium text-muted-foreground" data-id="element-1432" data-mp="2zfygb8qw11p8">
              {note.authorName} · {formatDateTime(note.createdAt)}
            </span>
            {note.body}
          </span>, document.body)}
    </span>;
}
function useWorkspaceMembers() {
  const {
    members
  } = useAuth();
  return members;
}